import { useState, useEffect } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { SSO_URL } from '../config/api';
import toast from 'react-hot-toast';
import './Login.css';

function Login() {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check for SSO data in URL
    const urlParams = new URLSearchParams(window.location.search);
    const ssoParam = urlParams.get('sso');
    
    if (ssoParam) {
      try {
        const ssoData = JSON.parse(decodeURIComponent(ssoParam));
        handleSSOAuthentication(ssoData);
        
        // Clear the URL parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } catch (error) {
        console.error('Error parsing SSO data:', error);
      }
    }

    // Listen for postMessage from SSO popup
    const handleMessage = (event) => {
      if (event.origin !== SSO_URL) {
        return;
      }
      
      const data = event.data;
      
      if (data && data.token && data.tokenType === 'custom_token') {
        handleSSOAuthentication(data);
      } else if (data && data.success === false) {
        console.error('SSO error received:', data.error);
        toast.error('SSO authentication failed: ' + data.error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleSSOAuthentication = async (ssoData) => {
    if (!ssoData || !ssoData.token) {
      console.error('Invalid SSO data');
      return;
    }

    try {
      setLoading(true);
      await signInWithCustomToken(auth, ssoData.token);
      
      // Check if user exists
      const userRef = doc(db, 'users', ssoData.user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        await auth.signOut();
        return;
      }
      
      // Store SSO data for later use
      localStorage.setItem('ssoData', JSON.stringify(ssoData));
      
      // Reload to update UI
      window.location.reload();
    } catch (error) {
      console.error('SSO authentication failed:', error);
      toast.error('SSO authentication failed. Please try logging in again.');
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const ssoUrl = `${SSO_URL}?popup=true&parent=${encodeURIComponent(window.location.origin)}`;
      const popup = window.open(ssoUrl, 'SSO Login', 'width=500,height=600,scrollbars=yes,resizable=yes');
      
      // Wait for SSO authentication result
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('SSO authentication timeout'));
        }, 60000);
        
        const handler = (event) => {
          if (event.origin !== SSO_URL) {
            return;
          }
          
          const data = event.data;
          
          if (data && data.token && data.tokenType === 'custom_token') {
            clearTimeout(timeout);
            window.removeEventListener('message', handler);
            resolve(data);
          } else if (data && data.success === false) {
            clearTimeout(timeout);
            window.removeEventListener('message', handler);
            reject(new Error(data.error || 'SSO authentication failed'));
          }
        };
        
        window.addEventListener('message', handler);
      });
      
      if (popup && !popup.closed) {
        popup.close();
      }
    } catch (error) {
      console.error('SSO authentication error:', error);
      toast.error('SSO authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-section">
      <div className="login-container">
        <div className="login-header">
          <div className="logo-container">
            <i className="fas fa-envelope"></i>
            <span className="brand-name">ITCPR WebMail</span>
          </div>
          <p className="subtitle">Sign in to your ITCPR institutional email</p>
        </div>
        
        <div className="sso-options">
          <button 
            id="sso-login" 
            className="sso-button primary-sso" 
            onClick={signInWithGoogle}
            disabled={loading}
          >
            <i className="fas fa-sign-in-alt"></i>
            <span>{loading ? 'Signing in...' : 'Sign in with SSO'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
